// DPoP 키 쌍과 관련 함수들
let dpopKeyPair: CryptoKeyPair | null = null;

/**
 * DPoP 키 쌍 생성 (브라우저 Web Crypto API 사용)
 */
export async function generateDPoPKeyPair(): Promise<CryptoKeyPair> {
  if (dpopKeyPair) {
    return dpopKeyPair;
  }

  dpopKeyPair = await crypto.subtle.generateKey(
    {
      name: 'ECDSA',
      namedCurve: 'P-256',
    },
    true,
    ['sign', 'verify']
  );

  return dpopKeyPair;
}

/**
 * JWK (JSON Web Key) 형식으로 공개키 내보내기
 */
export async function exportPublicKeyAsJWK(keyPair: CryptoKeyPair): Promise<JsonWebKey> {
  const jwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  return jwk;
}

/**
 * Base64 URL 인코딩
 */
function base64URLEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Simple SHA-256 hash implementation for DPoP
 */
async function sha256(data: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  return await crypto.subtle.digest('SHA-256', dataBuffer);
}

/**
 * DPoP proof JWT 생성
 */
export async function createDPoPProof(
  htm: string, // HTTP method (GET, POST, etc.)
  htu: string, // HTTP URI (target URL)
  accessToken?: string // 선택적: API 호출 시 액세스 토큰 바인딩
): Promise<string> {
  // 키 쌍이 없으면 생성
  if (!dpopKeyPair) {
    dpopKeyPair = await generateDPoPKeyPair();
  }

  // JWK 형식으로 공개키 가져오기
  const jwk = await exportPublicKeyAsJWK(dpopKeyPair);

  // JWT 헤더
  const header = {
    typ: 'dpop+jwt',
    alg: 'ES256',
    jwk: jwk
  };

  // JWT 페이로드
  const payload = {
    jti: crypto.randomUUID(), // 고유 ID
    htm: htm.toUpperCase(),
    htu: htu,
    iat: Math.floor(Date.now() / 1000),
    ...(accessToken && { ath: base64URLEncode(await sha256(accessToken)) })
  };

  // Base64 URL 인코딩
  const encodedHeader = base64URLEncode(new TextEncoder().encode(JSON.stringify(header)));
  const encodedPayload = base64URLEncode(new TextEncoder().encode(JSON.stringify(payload)));

  // 서명할 데이터
  const dataToSign = `${encodedHeader}.${encodedPayload}`;
  const signature = await crypto.subtle.sign(
    {
      name: 'ECDSA',
      hash: 'SHA-256',
    },
    dpopKeyPair.privateKey,
    new TextEncoder().encode(dataToSign)
  );

  const encodedSignature = base64URLEncode(signature);

  // 최종 JWT
  return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
}

/**
 * DPoP 헤더를 포함한 요청 옵션 생성
 */
export async function createDPoPHeaders(
  method: string,
  url: string,
  accessToken?: string,
  existingHeaders?: Record<string, string>
): Promise<Record<string, string>> {
  const dpopProof = await createDPoPProof(method, url, accessToken);
  
  return {
    ...existingHeaders,
    'DPoP': dpopProof
  };
}

/**
 * DPoP 지원 확인 (선택적)
 * 
 * Spring Authorization Server가 DPoP 토큰 발급을 지원하지 않으므로 
 * DPoP 기능을 비활성화합니다.
 */
export function isDPoPSupported(): boolean {
  // DPoP 기능 비활성화 - Spring Authorization Server는 DPoP 토큰 발급 미지원
  return false;
  
  // 기존 Web Crypto API 지원 확인 코드 (주석 처리)
  // return !!(
  //   window.crypto &&
  //   window.crypto.subtle &&
  //   typeof window.crypto.subtle.generateKey === 'function' &&
  //   typeof window.crypto.subtle.sign === 'function'
  // );
}